/**
 * SOChatterLogs — Odoo-faithful chatter log renderer for Sales Orders.
 * Matches Odoo's visual design: date separators, user avatars, status change
 * arrows, plain notes, and full email card rendering.
 */
import React, { useState, useEffect } from 'react';
import { crmAPI } from '../../lib/api';
import { Skeleton } from '../ui/skeleton';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

// ── Helpers ───────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  '#7C3AED','#DC2626','#D97706','#059669','#2563EB','#DB2777',
  '#0891B2','#65A30D','#9333EA','#EA580C','#0D9488','#4F46E5',
];

function avatarColor(name = '') {
  const code = [...name].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

function initials(name = '') {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (name[0] || '?').toUpperCase();
}

function relativeTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 31) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
  return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
}

function formatGroupDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });
}

function groupByDate(logs) {
  const groups = {};
  for (const log of logs) {
    const key = (log.date || '').slice(0, 10);
    if (!groups[key]) groups[key] = [];
    groups[key].push(log);
  }
  return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function Avatar({ name }) {
  const bg = avatarColor(name);
  return (
    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm"
      style={{ backgroundColor: bg }}>
      {initials(name)}
    </div>
  );
}

function DateSeparator({ date }) {
  return (
    <div className="flex items-center gap-3 my-4 select-none">
      <div className="flex-1 h-px bg-border" />
      <span className="text-xs font-medium text-muted-foreground whitespace-nowrap px-1">
        {formatGroupDate(date)}
      </span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

/** Parse Odoo tracking HTML and return an array of {field, old, new} changes */
function parseTrackingHTML(html = '') {
  if (!html) return [];
  const changes = [];
  // Match: text → text (Field) or list items with tracking classes
  // Odoo format: <li>Draft Sales Order → <span style="...">Sales Order</span> <span class="...">(Status)</span></li>
  const liMatches = html.match(/<li[^>]*>([\s\S]*?)<\/li>/g) || [];
  for (const li of liMatches) {
    const stripped = li.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    // Pattern: "old → new (FieldName)"
    const m = stripped.match(/^(.+?)\s*[\u2192>]+\s*(.+?)\s*\((.+?)\)\s*$/);
    if (m) {
      changes.push({ old: m[1].trim(), new: m[2].trim(), field: m[3].trim() });
    } else if (stripped.includes('→') || stripped.includes('->')) {
      // Simple "A → B" without field label
      const parts = stripped.split(/[→\->]+/);
      if (parts.length >= 2) {
        changes.push({ old: parts[0].trim(), new: parts[1].trim(), field: '' });
      }
    }
  }
  // If no list items found, try to find arrow in whole body
  if (changes.length === 0 && (html.includes('→') || html.includes('-&gt;'))) {
    const plain = html.replace(/<[^>]+>/g, ' ').replace(/&[a-z]+;/g, c =>
      ({'&gt;':'>','&lt;':'<','&amp;':'&','&nbsp;':' '}[c]||c)
    ).replace(/\s+/g, ' ').trim();
    const m = plain.match(/(.+?)\s*→\s*(.+?)(?:\s*\((.+?)\))?\s*$/);
    if (m) changes.push({ old: m[1].trim(), new: m[2].trim(), field: m[3]?.trim() || '' });
  }
  return changes;
}

/** Full email card renderer — matches Odoo's email display */
function EmailCard({ body, author, subjectLine }) {
  const [expanded, setExpanded] = useState(false);
  // Extract subject from body if not provided
  const subject = subjectLine || (() => {
    const m = (body || '').match(/subject[:\s]*([^\n<]+)/i);
    return m ? m[1].trim() : '';
  })();

  return (
    <div className="mt-1">
      {/* Subject header row — clickable to expand */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-3 py-2 rounded-t-lg bg-[#e8f4f8] border border-[#b8dde8] text-sm font-medium text-gray-700 italic flex items-center justify-between hover:bg-[#daeef6] transition-colors"
        data-testid="so-log-email-toggle"
      >
        <span>Subject: {subject || 'Sales Order Notification'}</span>
        <span className="text-xs text-muted-foreground ml-2">{expanded ? '▲' : '▼'}</span>
      </button>
      {expanded && (
        <div className="border border-t-0 border-[#b8dde8] rounded-b-lg overflow-hidden bg-white">
          <div
            className="p-4 text-sm email-body-content"
            style={{ maxHeight: '480px', overflowY: 'auto' }}
            dangerouslySetInnerHTML={{ __html: body || '' }}
          />
        </div>
      )}
    </div>
  );
}

/** Plain text / note renderer */
function NoteBody({ bodyClean, body }) {
  if (!bodyClean && !body) return null;
  // Try to detect multi-line notes
  const lines = (bodyClean || '').split('\n').filter(Boolean);
  if (lines.length <= 1 && bodyClean) {
    return <p className="text-sm text-gray-800 mt-0.5 leading-relaxed">{bodyClean}</p>;
  }
  return (
    <div className="text-sm text-gray-800 mt-0.5 leading-relaxed space-y-0.5">
      {lines.map((l, i) => <p key={i}>{l}</p>)}
    </div>
  );
}

/** Status change entry renderer */
function StatusChangeEntry({ log }) {
  const changes = parseTrackingHTML(log.body || '');
  const subtype = log.subtype || '';
  // Main label: "Quotation confirmed" comes from subtype or body_clean first line
  const label = subtype || (log.body_clean || '').split('\n')[0] || '';

  return (
    <div>
      {label && (
        <p className="text-sm text-gray-700 font-medium">{label}</p>
      )}
      {changes.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {changes.map((c, i) => (
            <li key={i} className="flex items-center gap-1.5 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-400 shrink-0" />
              <span className="text-gray-600">{c.old}</span>
              <span className="text-[#0ea5e9] font-bold">→</span>
              <span className="text-[#0ea5e9] font-semibold">{c.new}</span>
              {c.field && (
                <span className="text-gray-400 text-xs">({c.field})</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {changes.length === 0 && log.body_clean && !label && (
        <p className="text-sm text-gray-700">{log.body_clean}</p>
      )}
    </div>
  );
}

/** Single log entry */
function LogEntry({ log }) {
  const isEmail     = log.is_email || ['email','email_outgoing'].includes(log.message_type);
  const isStatus    = log.is_status_change && !isEmail;
  const isNote      = !isEmail && !isStatus;

  return (
    <div className="flex gap-3 py-2">
      <Avatar name={log.author || 'System'} />
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-semibold text-sm text-gray-900">{log.author}</span>
          <span className="text-xs text-gray-400">{relativeTime(log.date)}</span>
        </div>

        {/* Content */}
        {isEmail   && <EmailCard body={log.body} author={log.author} subjectLine={log.subject} />}
        {isStatus  && <StatusChangeEntry log={log} />}
        {isNote    && <NoteBody bodyClean={log.body_clean} body={log.body} />}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function SOChatterLogs({ soId, soNumber }) {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded]   = useState(false);

  const load = async () => {
    if (!soId) return;
    setLoading(true);
    try {
      const res = await crmAPI.getSalesOrderLogs(soId);
      setLogs(res.data || []);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
  }, [soId]);   // eslint-disable-line

  if (loading && !loaded) {
    return (
      <div className="space-y-4 py-2">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="w-9 h-9 rounded-full shrink-0" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const grouped = groupByDate(logs);

  return (
    <div data-testid="so-chatter-logs">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Chatter
          </span>
          {logs.length > 0 && (
            <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full">
              {logs.length}
            </span>
          )}
        </div>
        <Button variant="ghost" size="sm" className="h-6 text-[10px] text-muted-foreground" onClick={load}>
          <RefreshCw className="h-3 w-3 mr-1" />Refresh
        </Button>
      </div>

      {logs.length === 0 && loaded && (
        <div className="text-center py-8 text-muted-foreground text-sm">
          <p>No chatter logs found for this SO.</p>
          <p className="text-xs mt-1 text-muted-foreground/60">
            Logs are synced from Odoo every 5 minutes.
          </p>
        </div>
      )}

      {grouped.map(([dateKey, entries]) => (
        <div key={dateKey}>
          <DateSeparator date={dateKey} />
          {entries.map((log, i) => (
            <LogEntry key={log.date + log.author + i} log={log} />
          ))}
        </div>
      ))}
    </div>
  );
}
